import { invariant } from '@wyz/utils';
import { omit } from 'lodash-es';
import { CUSTOMER_LEVEL } from '@wyz/types';
import {
  IApplicationContext,
  ICustomer,
  TranslationKeys,
} from '~/common/types';
import { ENTITIES_STATUSES } from '~/common/schemas';
import {
  CustomerFormStep,
  ICriteriaList,
  ICustomerCreatePayload,
  ICustomerForm,
  ISupplierPlatform,
} from '~/connect/types';
import { CustomerFormPrivileges } from '~/connect/hooks';

// Dev's note : The order is very important, we use this array as data source for the UI Stepper
export const CUSTOMER_FORM_STEPS = [
  'GENERAL_FORM',
  'CRITERIA_FORM',
  'QUOTE_EXPRESS_FORM',
  'CONTACT_FORM',
  'DELIVERY_FORM',
  'CONFIGURATION_FORM',
  'SUPPLIERS_FORM',
  'BRANDS_FORM',
  'QUOTE_BRANDS_FORM',
  'REDISTRIBUTION_BRANDS_FORM',
  'SETTING_TEMPLATE_FORM',
  'INTERNAL_STOCK_FORM',
] as const;

export const getAuthorizedSteps = (
  customerFormPrivileges: CustomerFormPrivileges,
) => {
  return CUSTOMER_FORM_STEPS.filter(
    (step) => customerFormPrivileges[step].visible,
  );
};

export const READ_ONLY_CUSTOMER_STEPS: Array<CustomerFormStep> = [
  'BRANDS_FORM',
  'QUOTE_BRANDS_FORM',
  'REDISTRIBUTION_BRANDS_FORM',
  'SETTING_TEMPLATE_FORM',
];
export const CUSTOMER_STEP_LABELS: Record<
  CustomerFormStep,
  keyof TranslationKeys
> = {
  GENERAL_FORM: 'app.entity.tab.general',
  CRITERIA_FORM: 'app.common.criteria',
  QUOTE_EXPRESS_FORM: 'app.entity.tab.quote',
  CONTACT_FORM: 'app.entity.form.contact',
  DELIVERY_FORM: 'app.entity.tab.delivery',
  CONFIGURATION_FORM: 'app.entity.tab.config',
  SUPPLIERS_FORM: 'app.common.suppliers',
  BRANDS_FORM: 'app.entity.tab.brands',
  QUOTE_BRANDS_FORM: 'app.entity.tab.quote_brands',
  REDISTRIBUTION_BRANDS_FORM: 'app.entity.tab.redistribution_brands',
  SETTING_TEMPLATE_FORM: 'app.entity.tab.setting',
  INTERNAL_STOCK_FORM: 'app.entity.tab.my_stocks',
};

export const getStepLabelByKey = (stepKey: CustomerFormStep) => {
  return CUSTOMER_STEP_LABELS[stepKey];
};
export const GENERAL_FORM_STEP = 0;
export const CRITERIA_STEP = 1;
// export const SETTING_TEMPLATE_STEP = 10;
// export const CUSTOMER_FINAL_STEP = SETTING_TEMPLATE_STEP; // SETTING_FORM

const getCriterionValueById = (
  criterionId: string,
  criterionValueId: string,
  allCriteria: ICriteriaList['data'],
): ICustomerCreatePayload['criteria'][number]['value'] => {
  const criterion = allCriteria.find((item) => item.id === criterionId);
  invariant(criterion);
  const value = criterion.values.find(
    (criterionValue) => criterionValueId === criterionValue.id,
  );
  invariant(value);
  return value;
};
export const formToPayload = (
  formData: ICustomerForm,
  allCriteria: ICriteriaList['data'],
  entityLevel?: number,
): ICustomerCreatePayload => {
  return omit(
    {
      ...formData,
      code: formData.customerCode, // we need to rename 'customerCode' back to 'code' for the payload that will be sent to the server
      level: undefined, // level is always manager by the server (Tree extension)
      status: ENTITIES_STATUSES.ENABLED,
      parent: formData.parent?.id?.value
        ? { id: formData.parent.id.value }
        : undefined,
      logo: formData.logo?.id ? { id: formData.logo.id } : undefined,
      customerGroup:
        formData.customerGroup?.label && formData.customerGroup?.value
          ? {
              id: Number(formData.customerGroup.value),
              name: formData.customerGroup.label,
            }
          : null,
      criteria: formData.criteria.map((associatedCriterion) => ({
        criterion: { id: associatedCriterion.criterion.id },
        value: getCriterionValueById(
          associatedCriterion.criterion.id,
          associatedCriterion.value,
          allCriteria,
        ),
      })),
      billingInformation: {
        ...formData.billingInformation,
        billingCode:
          entityLevel !== CUSTOMER_LEVEL.N1 &&
          !formData.billingInformation.billingCode
            ? formData.customerCode
            : formData.billingInformation.billingCode,
      },
      internalStocks: formData.internalStocks.map(
        (stock, index) =>
          ({
            supplierId: stock.id,
            rank: index,
          }) satisfies ICustomerCreatePayload['internalStocks'][number],
      ),
      quoteInformation: {
        ...formData.quoteInformation,
        express: Boolean(formData.quoteInformation.express),
      },
    },
    ['_requiredCustomerGroup', '_requiredParent', 'customerCode'],
  );
};

export const DEFAULT_VALUES: Partial<ICustomerForm> = {
  criteria: [],
  contacts: [],
  legalForm: '',
  rcsNumber: '',
  vatNumber: '',
  quoteInformation: {
    diagnosisProvider: null,
    target: null,
    express: 0,
  },
  deliveryInformation: {
    address: '',
    city: '',
    zipCode: '',
    additionalAddress: '',
    country: undefined,
  },
  billingInformation: {} as ICustomerForm['billingInformation'],
  supplierConfigurations: [],
  configuration: { eligibleToContainerOrders: false },
  internalStocks: [],
  redistributionMaxDelay: null,
};

export const rawToForm = (
  entity: ICustomer,
  allCriteria: ICriteriaList['data'],
  allSupplierPlatforms: Array<ISupplierPlatform>,
): ICustomerForm => {
  const supplierPlatformById = allSupplierPlatforms.reduce(
    (acc, supplierPlatform) => {
      acc[supplierPlatform.supplier.id] = supplierPlatform;
      return acc;
    },
    {} as Record<number, (typeof allSupplierPlatforms)[number]>,
  );
  return {
    ...entity,
    customerCode: entity.code, // we need to rename 'code' to 'customerCode' for the form that will be used in the frontend to avoid problems with some chrome extensions that automatically clean up fields named 'code'
    legalForm: entity.legalForm ?? '',
    rcsNumber: entity.rcsNumber ?? '',
    vatNumber: entity.vatNumber ?? '',
    parent: entity.parent
      ? {
          id: {
            label: entity.parent.name,
            value: entity.parent.id,
            code: entity.parent.code,
          },
        }
      : undefined,
    customerGroup: {
      label: entity.customerGroup?.name,
      value: String(entity.customerGroup?.id),
    },
    criteria: allCriteria.map((criterion) => {
      const associatedCriterion = entity.criteria.find(
        (_associatedCriterion) =>
          _associatedCriterion.criterion.id === criterion.id,
      );
      return {
        criterion: criterion,
        // it's okay to type assert here, because we'll have form validation that will prevent the submit of form
        // but when initializing the form we need to set the undefined value since the customer doesn't have a value for this newly created criteria
        value: associatedCriterion?.value.id as string,
      };
    }),
    internalStocks: entity.internalStocks
      .filter((stock) => supplierPlatformById[stock.supplierId])
      .map((stock) => ({
        id: stock.supplierId,
        name: supplierPlatformById[stock.supplierId].supplier.name,
      })),
    quoteInformation: {
      ...entity.quoteInformation,
      express: Number(!!entity.quoteInformation?.express) as 0 | 1,
    },
  };
};
export const getCreateDefaultForm = (
  userEntities: IApplicationContext['entities'],
): Partial<ICustomerForm> => {
  return {
    ...DEFAULT_VALUES,
    parent:
      userEntities.length === 1
        ? {
            id: {
              value: userEntities[0].id,
              label: userEntities[0].name,
              code: userEntities[0].code,
            },
          }
        : undefined,
  };
};

export const canSetContactAsDefault = (
  contacts: Array<{ types: string[]; default: boolean }>,
  contactIndex: number,
): boolean => {
  const currentTypes = contacts[contactIndex].types;

  return !contacts.some(
    (contact, i) =>
      i !== contactIndex &&
      contact.default &&
      contact.types.some((type) => currentTypes.includes(type)),
  );
};
